import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, FileText, Menu, X, ChevronRight } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useTema, DEFAULT_LOGO } from '@/lib/TemaContext';
import { usePublicRoutes } from '@/lib/publicRoutes';
import { useCdiAge } from '@/hooks/useCdiAge';
import MobileDock from '@/components/MobileDock';
import { PHONE_HREF } from '@/lib/contactActions';
import { optimizeImageUrl, restoreOriginalImage } from '@/lib/imageUrl';

export default function Navbar() {
  const cdiAge = useCdiAge();
  const { config } = useTema();
  const logoUrl = config?.logo_url || DEFAULT_LOGO;
  const optimizedLogoUrl = optimizeImageUrl(logoUrl, { width: 320, quality: 80, resize: 'contain' });
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { path, label } = usePublicRoutes();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    ['home', 'HOME'],
    ['exams', 'EXAMES'],
    ['insurance', 'CONVÊNIOS'],
    ['about', 'SOBRE'],
    ['doctors', 'CORPO CLÍNICO'],
    ['careers', 'TRABALHE CONOSCO'],
    ['blog', 'BLOG'],
    ['faq', 'FAQ'],
    ['contact', 'CONTATO'],
  ].map(([pageKey, desktopLabel]) => ({
    pageKey,
    desktopLabel,
    label: label(pageKey),
    path: path(pageKey),
  }));

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path;
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50">
        {/* ── Primary row: logo + CTAs em evidência ── */}
        <div className="bg-cdi-blue">
          <div className="max-w-7xl mx-auto flex items-center px-4 md:px-6 h-20 lg:h-24">
            {/* Logo */}
            <Link to={path('home')} className="mr-auto flex h-12 w-[196px] items-center transition-transform hover:scale-[1.02] lg:h-16 lg:w-[230px]">
              <img
                src={optimizedLogoUrl}
                alt={`CDI Premium — ${cdiAge} anos`}
                width="563"
                height="125"
                decoding="async"
                onError={(event) => restoreOriginalImage(event, logoUrl)}
                className="h-full w-full object-contain object-left"
              />
            </Link>

            {/* CTA buttons (desktop) */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href={PHONE_HREF}
                className="flex items-center gap-2.5 px-4 py-2.5 bg-cdi-yellow hover:translate-y-[-2px] transition-transform"
                style={{ boxShadow: '4px 4px 0px rgba(255,255,255,0.15)' }}
              >
                <Phone size={18} strokeWidth={2.5} className="text-cdi-blue" />
                <div className="text-left leading-tight">
                  <p className="text-[10px] font-bold text-cdi-blue uppercase tracking-wider">Fale Conosco</p>
                  <p className="text-sm font-black text-cdi-blue">3121-7272</p>
                </div>
              </a>
              <Link
                to={path('schedule')}
                className="flex items-center gap-2.5 px-4 py-2.5 bg-white hover:translate-y-[-2px] transition-transform"
                style={{ boxShadow: '4px 4px 0px rgb(var(--cdi-yellow))' }}
              >
                <WhatsAppIcon size={18} className="text-green-600" />
                <div className="text-left leading-tight">
                  <p className="text-[10px] font-bold text-cdi-carbon/60 uppercase tracking-wider">Agendamento</p>
                  <p className="text-sm font-black text-cdi-carbon">pelo WhatsApp</p>
                </div>
              </Link>
              <Link
                to={path('portal')}
                className="flex items-center gap-2.5 px-4 py-2.5 bg-white hover:translate-y-[-2px] transition-transform"
                style={{ boxShadow: '4px 4px 0px rgb(var(--cdi-yellow))' }}
              >
                <FileText size={18} strokeWidth={2.5} className="text-cdi-blue" />
                <div className="text-left leading-tight">
                  <p className="text-[10px] font-bold text-cdi-carbon/60 uppercase tracking-wider">Resultados</p>
                  <p className="text-sm font-black text-cdi-carbon">de Exames</p>
                </div>
              </Link>
            </div>

            {/* Hamburger (mobile) */}
            <button
              className="lg:hidden flex items-center justify-center w-11 h-11 border-2 border-cdi-yellow text-cdi-yellow"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* ── Secondary row: lista de navegação ── */}
        <div className={`bg-white border-b-2 border-cdi-blue/10 ${scrolled ? 'hidden' : 'hidden lg:block'}`}>
          <nav className="max-w-7xl mx-auto flex items-stretch justify-center h-11 px-6" aria-label="Navegação principal">
            {navLinks.map((link) => (
              <Link
                key={link.pageKey}
                to={link.path}
                className={`flex items-center px-5 text-[11px] font-black tracking-widest transition-colors relative group ${
                  isActive(link.path) ? 'text-cdi-blue' : 'text-cdi-carbon/60 hover:text-cdi-blue'
                }`}
              >
                {link.desktopLabel}
                <span className={`absolute bottom-0 left-0 right-0 h-1 bg-cdi-yellow transition-transform duration-300 ${
                  isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`} />
              </Link>
            ))}
            <Link
              to="/intra"
              className={`flex items-center px-5 text-[11px] font-black tracking-widest transition-colors relative group ${
                isActive('/intra') ? 'text-cdi-blue' : 'text-cdi-carbon/60 hover:text-cdi-blue'
              }`}
            >
              LOGIN
              <span className={`absolute bottom-0 left-0 right-0 h-1 bg-cdi-yellow transition-transform duration-300 ${
                isActive('/intra') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`} />
            </Link>
          </nav>
        </div>
      </header>

      {/* Spacer to offset fixed header */}
      <div className={`h-20 ${scrolled ? 'lg:h-24' : 'lg:h-[140px]'}`} />

      {/* Full-screen overlay menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-cdi-blue flex flex-col pt-20"
          >
            <nav className="flex-1 overflow-y-auto px-6 py-4" aria-label="Navegação mobile">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.pageKey}
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 border-b border-white/15 group"
                  >
                    <span className={`text-xl font-black tracking-tight transition-colors ${isActive(link.path) ? 'text-cdi-yellow' : 'text-white group-hover:text-cdi-yellow'}`}>
                      {link.label}
                    </span>
                    <ChevronRight size={18} className={`transition-colors ${isActive(link.path) ? 'text-cdi-yellow' : 'text-white/30 group-hover:text-cdi-yellow'}`} />
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ x: -40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: navLinks.length * 0.05 }}
              >
                <Link
                  to="/intra"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between py-3.5 border-b border-white/15 group"
                >
                  <span className="text-xl font-black tracking-tight text-white group-hover:text-cdi-yellow transition-colors">LOGIN</span>
                  <ChevronRight size={18} className="text-white/30 group-hover:text-cdi-yellow transition-colors" />
                </Link>
              </motion.div>
            </nav>
            <div className="p-5 grid grid-cols-3 gap-2.5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
              <a
                href={PHONE_HREF}
                className="flex flex-col items-center justify-center gap-1.5 py-3.5 bg-white/10 border-2 border-white/25 active:bg-white/20 transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <Phone size={18} className="text-cdi-yellow" />
                <span className="text-white font-bold text-[10px] uppercase tracking-wider">Ligar</span>
              </a>
              <Link
                to={path('schedule')}
                className="flex flex-col items-center justify-center gap-1.5 py-3.5 bg-white/10 border-2 border-white/25 active:bg-white/20 transition-colors"
                onClick={() => setMenuOpen(false)}
                aria-label="Agendar pelo WhatsApp preenchendo o formulário"
              >
                <WhatsAppIcon size={18} className="text-cdi-yellow" />
                <span className="text-white font-bold text-[10px] uppercase tracking-wider">Agendar</span>
              </Link>
              <Link
                to={path('portal')}
                className="flex flex-col items-center justify-center gap-1.5 py-3.5 bg-cdi-yellow active:bg-cdi-yellow/90 transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <FileText size={18} className="text-cdi-blue" />
                <span className="text-cdi-blue font-black text-[10px] tracking-wider text-center">{label('portal')}</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MobileDock hidden={menuOpen} />
    </>
  );
}
